import {
  buildDeviceInfo,
  collectDeviceCapabilities,
  getResolution,
  getViewportInfo,
  DeviceType,
  type DeviceCapabilitiesPayload,
} from "@onmo/eon-streaming";
import {
  DeviceInfoInput,
  DeviceType as BackendDeviceType,
} from "@onmo/graphql/graphql";
import { isAndroidAppTv, isNativeApp, isTizen, isWebos } from "./platform";

const hasTizenRuntime = (): boolean =>
  window.webapis?.productinfo !== undefined;

const hasWebosRuntime = (): boolean =>
  (window as Window & { webOS?: { deviceInfo?: unknown } }).webOS
    ?.deviceInfo !== undefined;

/**
 * Detect the current device type based on platform and user agent.
 */
export function getDeviceType(): DeviceType {
  const ua = navigator.userAgent;

  if (isTizen || hasTizenRuntime() || /tizen/i.test(ua)) return "SamsungTV";
  if (isWebos || hasWebosRuntime() || /web[o0]s/i.test(ua)) return "LGTV";

  if (/\bCrOS\b/i.test(ua)) return "Desktop";
  if (
    isAndroidAppTv ||
    /\bAFT[A-Z0-9]|BRAVIA|Android ?TV|Google ?TV|CrKey|MiBOX|MiTV/i.test(ua)
  )
    return "AndroidTV";

  // tablet
  const isIpadOS = /macintosh/i.test(ua) && navigator.maxTouchPoints > 1;
  if (/tablet|ipad|playbook|silk/i.test(ua) || isIpadOS) return "Tablet";

  // mobile
  if (/mobile/i.test(ua)) return "Mobile";

  if (/Android/i.test(ua)) return "Tablet";

  return "Desktop";
}

export function getBackendDeviceType(): BackendDeviceType {
  const type = getDeviceType();
  if (type === "AndroidTV" || type === "SamsungTV" || type === "LGTV") {
    return BackendDeviceType.Tv;
  }
  switch (type) {
    case "Mobile":
      return isNativeApp
        ? BackendDeviceType.MobileNative
        : BackendDeviceType.MobileWeb;
    case "Tablet":
      return isNativeApp
        ? BackendDeviceType.TabletNative
        : BackendDeviceType.TabletWeb;
    case "Desktop":
      return isNativeApp
        ? BackendDeviceType.DesktopNative
        : BackendDeviceType.DesktopWeb;
    default:
      return BackendDeviceType.Unknown;
  }
}

export function getDeviceInfo(): DeviceInfoInput {
  return buildDeviceInfo(getBackendDeviceType());
}

let capabilitiesPromise: Promise<DeviceCapabilitiesPayload> | null = null;

export function prefetchDeviceCapabilities(): Promise<DeviceCapabilitiesPayload> {
  if (!capabilitiesPromise) {
    capabilitiesPromise = collectDeviceCapabilities().catch((err) => {
      capabilitiesPromise = null; // don't cache a failure — let the next call retry
      throw err;
    });
  }
  return capabilitiesPromise;
}

export async function getDeviceInfoWithSpecs(
  playerEl?: Element | null,
): Promise<DeviceInfoInput> {
  const { spec, ...device } = await prefetchDeviceCapabilities();
  const viewport = getViewportInfo(playerEl);

  return {
    ...device,
    resolution: getResolution(),
    specs: { ...spec, ...(viewport ? { viewport } : {}) },
  };
}
